/**
 * Capitalization Inputs Component
 * Financial inputs for Capitalization of Earnings method
 * Includes Revenue, EBITDA, and Net Income with dollar/percentage toggles
 * EBITDA and Net Income are mutually exclusive
 * Includes Business Type Selection and Capitalization Rate input
 */
import React from 'react';
import PropTypes from 'prop-types';
import { HelpCircle, Info } from 'lucide-react';

const CapitalizationInputs = ({
  formData,
  onInputChange,
  onShowHelp,
  setFormData,
  formatNumberWithCommas
}) => {
  // Get or set default earnings type
  const capitalizationEarningsType = formData.capitalization_earnings_type || 'ebitda';

  return (
    <div className="col-span-full mb-4">
      {/* Earnings Type Selector - Similar to CCA Multiple Selection */}
      <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
        <h4 className="font-medium text-gray-900 mb-3">Select Earnings Metric for Capitalization</h4>
        <div>
          <select
            value={capitalizationEarningsType}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                capitalization_earnings_type: e.target.value
              }));
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="ebitda">EBITDA (Recommended)</option>
            <option value="net_income">Net Income</option>
            <option value="sde">SDE (Seller's Discretionary Earnings)</option>
          </select>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          <span className="font-semibold">Note:</span> You can enter all earnings metrics below. The selected metric will be used for the valuation calculation.
          {capitalizationEarningsType === 'ebitda' && ' EBITDA is typically preferred for business valuations as it reflects operational performance.'}
          {capitalizationEarningsType === 'net_income' && ' Net Income accounts for all expenses including taxes and interest.'}
          {capitalizationEarningsType === 'sde' && ' SDE includes owner compensation and benefits, commonly used for small business valuations.'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Annual Revenue */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Annual Revenue *
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onShowHelp('revenue');
              }}
              className="ml-1 text-blue-600 hover:text-blue-700"
            >
              <HelpCircle size={14} className="inline" />
            </button>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-3 text-gray-500">$</span>
            <input
              type="text"
              name="revenue"
              value={formatNumberWithCommas(formData.revenue)}
              onChange={onInputChange}
              required
              placeholder="5,000,000"
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* EBITDA with toggle */}
        <div className={capitalizationEarningsType === 'ebitda' ? 'ring-2 ring-purple-300 rounded-lg p-2 -m-2' : ''}>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
              EBITDA
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onShowHelp('ebitda');
                }}
                className="ml-1 text-blue-600 hover:text-blue-700"
              >
                <HelpCircle size={14} className="inline" />
              </button>
            </label>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="ebitda_input_type"
                  value="dollar"
                  checked={formData.ebitda_input_type === 'dollar'}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      ebitda_input_type: 'dollar',
                      ebitda_margin: ''
                    }));
                  }}
                  className="text-blue-600"
                />
                <span className="text-xs">$</span>
              </label>
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="ebitda_input_type"
                  value="percentage"
                  checked={formData.ebitda_input_type === 'percentage'}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      ebitda_input_type: 'percentage',
                      ebitda: ''
                    }));
                  }}
                  className="text-blue-600"
                />
                <span className="text-xs">%</span>
              </label>
            </div>
          </div>
          {formData.ebitda_input_type === 'dollar' ? (
            <>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">$</span>
                <input
                  type="text"
                  name="ebitda"
                  value={formatNumberWithCommas(formData.ebitda)}
                  onChange={onInputChange}
                  placeholder="1,000,000"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {formData.ebitda && formData.revenue && (
                <p className="text-xs text-gray-600 mt-1">
                  = {((parseFloat(formData.ebitda) / parseFloat(formData.revenue)) * 100).toFixed(1)}% margin
                </p>
              )}
            </>
          ) : (
            <>
              <div className="relative">
                <input
                  type="number"
                  name="ebitda_margin"
                  value={formData.ebitda_margin}
                  onChange={(e) => {
                    const margin = parseFloat(e.target.value) || 0;
                    const revenue = parseFloat(formData.revenue) || 0;
                    const calculatedEbitda = revenue > 0 && margin > 0 ? (revenue * margin / 100) : '';
                    setFormData(prev => ({
                      ...prev,
                      ebitda_margin: e.target.value,
                      ebitda: calculatedEbitda
                    }));
                  }}
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="20"
                  className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="absolute right-3 top-3 text-gray-500">%</span>
              </div>
              {formData.ebitda_margin && formData.revenue && (
                <p className="text-xs text-gray-600 mt-1">
                  = ${Math.round(parseFloat(formData.ebitda || 0)).toLocaleString()}
                </p>
              )}
            </>
          )}
        </div>

        {/* Net Income with toggle */}
        <div className={capitalizationEarningsType === 'net_income' ? 'ring-2 ring-purple-300 rounded-lg p-2 -m-2' : ''}>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
              Net Income
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onShowHelp('net_income');
                }}
                className="ml-1 text-blue-600 hover:text-blue-700"
              >
                <HelpCircle size={14} className="inline" />
              </button>
            </label>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="net_income_input_type"
                  value="dollar"
                  checked={formData.net_income_input_type === 'dollar'}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      net_income_input_type: 'dollar',
                      net_income_margin: ''
                    }));
                  }}
                  className="text-blue-600"
                />
                <span className="text-xs">$</span>
              </label>
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="net_income_input_type"
                  value="percentage"
                  checked={formData.net_income_input_type === 'percentage'}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      net_income_input_type: 'percentage',
                      net_income: ''
                    }));
                  }}
                  className="text-blue-600"
                />
                <span className="text-xs">%</span>
              </label>
            </div>
          </div>
          {formData.net_income_input_type === 'dollar' ? (
            <>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">$</span>
                <input
                  type="text"
                  name="net_income"
                  value={formatNumberWithCommas(formData.net_income)}
                  onChange={onInputChange}
                  placeholder="750,000"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {formData.net_income && formData.revenue && (
                <p className="text-xs text-gray-600 mt-1">
                  = {((parseFloat(formData.net_income) / parseFloat(formData.revenue)) * 100).toFixed(1)}% margin
                </p>
              )}
            </>
          ) : (
            <>
              <div className="relative">
                <input
                  type="number"
                  name="net_income_margin"
                  value={formData.net_income_margin}
                  onChange={(e) => {
                    const margin = parseFloat(e.target.value) || 0;
                    const revenue = parseFloat(formData.revenue) || 0;
                    const calculatedNetIncome = revenue > 0 && margin > 0 ? (revenue * margin / 100) : '';
                    setFormData(prev => ({
                      ...prev,
                      net_income_margin: e.target.value,
                      net_income: calculatedNetIncome
                    }));
                  }}
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="15"
                  className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="absolute right-3 top-3 text-gray-500">%</span>
              </div>
              {formData.net_income_margin && formData.revenue && (
                <p className="text-xs text-gray-600 mt-1">
                  = ${Math.round(parseFloat(formData.net_income || 0)).toLocaleString()}
                </p>
              )}
            </>
          )}
        </div>

        {/* SDE with toggle */}
        <div className={capitalizationEarningsType === 'sde' ? 'ring-2 ring-purple-300 rounded-lg p-2 -m-2' : ''}>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
              SDE
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onShowHelp('sde');
                }}
                className="ml-1 text-blue-600 hover:text-blue-700"
              >
                <HelpCircle size={14} className="inline" />
              </button>
            </label>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="sde_input_type"
                  value="dollar"
                  checked={formData.sde_input_type === 'dollar'}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      sde_input_type: 'dollar',
                      sde_margin: ''
                    }));
                  }}
                  className="text-blue-600"
                />
                <span className="text-xs">$</span>
              </label>
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="sde_input_type"
                  value="percentage"
                  checked={formData.sde_input_type === 'percentage'}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      sde_input_type: 'percentage',
                      sde: ''
                    }));
                  }}
                  className="text-blue-600"
                />
                <span className="text-xs">%</span>
              </label>
            </div>
          </div>
          {formData.sde_input_type === 'dollar' ? (
            <>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">$</span>
                <input
                  type="text"
                  name="sde"
                  value={formatNumberWithCommas(formData.sde)}
                  onChange={onInputChange}
                  placeholder="975,000"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {formData.sde && formData.revenue && (
                <p className="text-xs text-gray-600 mt-1">
                  = {((parseFloat(formData.sde) / parseFloat(formData.revenue)) * 100).toFixed(1)}% margin
                </p>
              )}
            </>
          ) : (
            <>
              <div className="relative">
                <input
                  type="number"
                  name="sde_margin"
                  value={formData.sde_margin}
                  onChange={(e) => {
                    const margin = parseFloat(e.target.value) || 0;
                    const revenue = parseFloat(formData.revenue) || 0;
                    const calculatedSde = revenue > 0 && margin > 0 ? (revenue * margin / 100) : '';
                    setFormData(prev => ({
                      ...prev,
                      sde_margin: e.target.value,
                      sde: calculatedSde
                    }));
                  }}
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="18"
                  className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="absolute right-3 top-3 text-gray-500">%</span>
              </div>
              {formData.sde_margin && formData.revenue && (
                <p className="text-xs text-gray-600 mt-1">
                  = ${Math.round(parseFloat(formData.sde || 0)).toLocaleString()}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Business Type Selection + Capitalization Rate */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
        {/* Business Type Selection - Left Column (3/4 width) */}
        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Business Type
            <span className="text-xs text-gray-500 ml-1">(Select one to auto-populate cap rate)</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Growth Business */}
            <div
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  business_type: 'growth',
                  capitalization_rate: '15'
                }));
              }}
              className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all group ${
                formData.business_type === 'growth'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <input
                    type="radio"
                    name="business_type"
                    value="growth"
                    checked={formData.business_type === 'growth'}
                    onChange={() => {}}
                    className="w-4 h-4 text-green-600 mt-0.5 mr-2"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">Growth</p>
                    <p className="text-xs text-gray-600 mt-1">Cap Rate Range: 10-20%</p>
                    <p className="text-sm text-green-700 font-medium mt-1">Default: 15%</p>
                  </div>
                </div>
                <Info size={16} className="text-gray-400 ml-2 flex-shrink-0" />
              </div>
              {/* Tooltip */}
              <div className="absolute left-0 top-full mt-2 w-full bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 shadow-lg">
                <p className="font-semibold mb-1">Select if your business:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Has strong revenue growth (20%+ annually)</li>
                  <li>Is in an expanding market</li>
                  <li>Has increasing profit margins</li>
                  <li>Demonstrates scalability potential</li>
                </ul>
              </div>
            </div>

            {/* Stable Business */}
            <div
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  business_type: 'stable',
                  capitalization_rate: '20'
                }));
              }}
              className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all group ${
                formData.business_type === 'stable'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <input
                    type="radio"
                    name="business_type"
                    value="stable"
                    checked={formData.business_type === 'stable'}
                    onChange={() => {}}
                    className="w-4 h-4 text-blue-600 mt-0.5 mr-2"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">Stable</p>
                    <p className="text-xs text-gray-600 mt-1">Cap Rate Range: 15-25%</p>
                    <p className="text-sm text-blue-700 font-medium mt-1">Default: 20%</p>
                  </div>
                </div>
                <Info size={16} className="text-gray-400 ml-2 flex-shrink-0" />
              </div>
              {/* Tooltip */}
              <div className="absolute left-0 top-full mt-2 w-full bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 shadow-lg">
                <p className="font-semibold mb-1">Select if your business:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Has consistent revenue (5-15% growth)</li>
                  <li>Operates in a mature market</li>
                  <li>Maintains steady profit margins</li>
                  <li>Has predictable cash flows</li>
                </ul>
              </div>
            </div>

            {/* Declining Business */}
            <div
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  business_type: 'declining',
                  capitalization_rate: '30'
                }));
              }}
              className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all group ${
                formData.business_type === 'declining'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <input
                    type="radio"
                    name="business_type"
                    value="declining"
                    checked={formData.business_type === 'declining'}
                    onChange={() => {}}
                    className="w-4 h-4 text-amber-600 mt-0.5 mr-2"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">Declining</p>
                    <p className="text-xs text-gray-600 mt-1">Cap Rate Range: 25-35%</p>
                    <p className="text-sm text-amber-700 font-medium mt-1">Default: 30%</p>
                  </div>
                </div>
                <Info size={16} className="text-gray-400 ml-2 flex-shrink-0" />
              </div>
              {/* Tooltip */}
              <div className="absolute left-0 top-full mt-2 w-full bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 shadow-lg">
                <p className="font-semibold mb-1">Select if your business:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Has declining revenue or flat growth</li>
                  <li>Faces strong competitive pressure</li>
                  <li>Operates in a shrinking market</li>
                  <li>Has decreasing profit margins</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Capitalization Rate - Right Column (1/4 width) */}
        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Capitalization Rate (%)
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onShowHelp('capitalization_rate');
              }}
              className="ml-1 text-blue-600 hover:text-blue-700"
            >
              <HelpCircle size={14} className="inline" />
            </button>
          </label>
          <div className="relative">
            <input
              type="number"
              name="capitalization_rate"
              value={formData.capitalization_rate}
              onChange={onInputChange}
              step="0.1"
              min="0"
              max="100"
              placeholder="20"
              className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="absolute right-3 top-3 text-gray-500">%</span>
          </div>
          <p className="text-xs text-gray-600 mt-1">Leave blank to use default Cap Rate of 20%</p>
        </div>
      </div>
    </div>
  );
};

CapitalizationInputs.propTypes = {
  formData: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onShowHelp: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
  formatNumberWithCommas: PropTypes.func.isRequired
};

export default CapitalizationInputs;
